import {
  createContext,
  use,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { ConfigCatProvider, DataGovernance, PollingMode } from 'configcat-react';
import { env } from 'customer-portal-helpers/env';

type FeatureFlagsContextValue = {
  flagsReady: boolean;
  flagsError: boolean;
  setFlagsReady: Dispatch<SetStateAction<boolean>>;
  setFlagsError: Dispatch<SetStateAction<boolean>>;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

export const useFlagsContext = () => {
  const context = use(FeatureFlagsContext);

  if (!context) {
    throw new Error('useFlagsContext must be used within a FeatureFlagsProvider');
  }

  return context;
};

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const [flagsReady, setFlagsReady] = useState(false);
  const [flagsError, setFlagsError] = useState(false);

  const CONFIGCAT_SDK_KEY = env('PORTAL_CONFIGCAT_SDK_KEY');

  if (!CONFIGCAT_SDK_KEY || typeof CONFIGCAT_SDK_KEY !== 'string') {
    throw Error('ConfigCat SDK key is not set');
  }

  const contextValue = useMemo(
    () => ({
      flagsReady,
      flagsError,
      setFlagsReady,
      setFlagsError,
    }),
    [flagsReady, flagsError],
  );

  return (
    <ConfigCatProvider
      sdkKey={CONFIGCAT_SDK_KEY}
      pollingMode={PollingMode.AutoPoll}
      options={{ dataGovernance: DataGovernance.EuOnly }}
    >
      <FeatureFlagsContext.Provider value={contextValue}>{children}</FeatureFlagsContext.Provider>
    </ConfigCatProvider>
  );
};
